import type { EAN } from '../shared/ean';
import type { OrgId, UserId } from '../shared/ids';
import type { SegmentId } from './segment-id';
import type { SkuPredicate } from './sku-predicate';

/** Who is looking: the signed-in user (null when unknown) and every org they are a member of. */
export type Viewer = {
  readonly userId: UserId | null;
  readonly orgIds: readonly OrgId[];
};

// ---------------------------------------------------------------------------
// SegmentDefinition: the ADR-0003 specification (spec + pins)
// ---------------------------------------------------------------------------

export type SegmentDefinition = {
  readonly spec: SkuPredicate;
  readonly include: ReadonlySet<EAN>;
  readonly exclude: ReadonlySet<EAN>;
};

export namespace SegmentDefinition {
  export function of(
    spec: SkuPredicate,
    include: Iterable<EAN> = [],
    exclude: Iterable<EAN> = []
  ): SegmentDefinition {
    return { spec, include: new Set(include), exclude: new Set(exclude) };
  }

  /**
   * The whole definition as one predicate: all(any(spec, ean-in(include)), not(ean-in(exclude))).
   * Membership is eval(toPredicate(d)) at point of use -- pins are a normalized region of
   * the filter, not a second membership channel.
   */
  export function toPredicate(definition: SegmentDefinition): SkuPredicate {
    const withPins: SkuPredicate =
      definition.include.size > 0
        ? {
            kind: 'any',
            predicates: [definition.spec, { kind: 'ean-in', eans: [...definition.include] }],
          }
        : definition.spec;
    if (definition.exclude.size === 0) return withPins;
    return {
      kind: 'all',
      predicates: [
        withPins,
        { kind: 'not', predicate: { kind: 'ean-in', eans: [...definition.exclude] } },
      ],
    };
  }

  /** The pins themselves, for the reads that ask about pins; membership is toPredicate. */
  export function pinnedEans(definition: SegmentDefinition): ReadonlySet<EAN> {
    return definition.include;
  }

  /** What `toPredicate` reads, keyed: spec and both pin sets, sorted. */
  export function authoredKey(definition: SegmentDefinition): string {
    return JSON.stringify([
      definition.spec,
      [...definition.include].sort(),
      [...definition.exclude].sort(),
    ]);
  }
}

// ---------------------------------------------------------------------------
// Segment: the stored aggregate (identity + tenancy + alias + definition)
// ---------------------------------------------------------------------------

/** Whether a member wrote the comparison or the platform curates it for the org (CONTEXT.md: Curated Comparison). */
type Provenance = 'authored' | 'curated';

export type Segment = {
  readonly id: SegmentId;
  readonly owner: UserId | null;
  readonly org: OrgId | null;
  readonly alias: string;
  readonly sharedWithOrg: boolean;
  readonly definition: SegmentDefinition;
  readonly provenance: Provenance;
};

export function Segment(params: {
  id: SegmentId;
  alias: string;
  owner: UserId | null;
  org: OrgId | null;
  sharedWithOrg: boolean;
  spec?: SkuPredicate;
  include?: Iterable<EAN>;
  exclude?: Iterable<EAN>;
  provenance?: Provenance;
}): Segment {
  if (params.owner === null && params.org === null) {
    throw new Error(
      `Invalid Segment: owner and org cannot both be null — violates portal_comparison_owner_check (id: "${params.id}")`
    );
  }
  const alias = params.alias.trim();
  if (!alias) {
    throw new Error(`Invalid Segment: empty alias (id: "${params.id}")`);
  }
  return {
    id: params.id,
    alias,
    owner: params.owner,
    org: params.org,
    sharedWithOrg: params.sharedWithOrg,
    definition: SegmentDefinition.of(params.spec ?? false, params.include, params.exclude),
    provenance: params.provenance ?? 'authored',
  };
}

export namespace Segment {
  export function toPredicate(segment: Segment): SkuPredicate {
    return SegmentDefinition.toPredicate(segment.definition);
  }

  export function pinnedEans(segment: Segment): ReadonlySet<EAN> {
    return SegmentDefinition.pinnedEans(segment.definition);
  }

  export function authoredKey(segment: Segment): string {
    return SegmentDefinition.authoredKey(segment.definition);
  }
}

export function withAlias(segment: Segment, alias: string): Segment {
  const trimmed = alias.trim();
  if (!trimmed) {
    throw new Error(`Invalid Segment alias: empty value (id: "${segment.id}")`);
  }
  if (trimmed === segment.alias) return segment;
  return { ...segment, alias: trimmed };
}

export function withSharing(segment: Segment, shared: boolean): Segment {
  if (segment.sharedWithOrg === shared) return segment;
  return { ...segment, sharedWithOrg: shared };
}

export function isOrgOwned(segment: Segment, orgId: OrgId | null): boolean {
  return segment.owner === null && orgId !== null && segment.org === orgId;
}

/** Whether the segment's org is one of the given orgs. */
export function isMemberOrg(segment: Segment, orgIds: readonly OrgId[]): boolean {
  return segment.org !== null && orgIds.includes(segment.org);
}

export function withSkusAdded(segment: Segment, eans: readonly EAN[]): Segment {
  const { include } = segment.definition;
  if (eans.every((ean) => include.has(ean))) return segment;
  return {
    ...segment,
    definition: { ...segment.definition, include: new Set([...include, ...eans]) },
  };
}

export function withSkusRemoved(segment: Segment, eans: readonly EAN[]): Segment {
  const { include } = segment.definition;
  if (!eans.some((ean) => include.has(ean))) return segment;
  const removed = new Set(eans);
  return {
    ...segment,
    definition: {
      ...segment.definition,
      include: new Set([...include].filter((ean) => !removed.has(ean))),
    },
  };
}

/**
 * Contents edit policy: may the viewer change the pins of a segment. Mirrors the database RPC
 * `segment_modify_pins` - the owner, or a member of the row's org on a row that is shared with
 * the org or org-owned. Provenance is not a clause: a curated row edits on the same terms as an
 * authored one (decided 2026-10-16).
 */
export function canEditContents(segment: Segment | null, viewer: Viewer): boolean {
  if (!viewer.userId || !segment) return false;
  if (segment.owner === viewer.userId) return true;
  return isMemberOrg(segment, viewer.orgIds) && (segment.sharedWithOrg || segment.owner === null);
}

/**
 * Manage policy: may the viewer rename, toggle sharing on or soft delete a segment. Mirrors the
 * RLS policy `comparison_edit` on `portal_comparison` - the owner, or a member of the row's org
 * on an org-owned row. Provenance is not a clause: a curated row is managed on the same terms as
 * an authored one (decided 2026-10-16).
 */
export function canManage(segment: Segment | null, viewer: Viewer): boolean {
  if (!viewer.userId || !segment) return false;
  if (segment.owner === viewer.userId) return true;
  return segment.owner === null && isMemberOrg(segment, viewer.orgIds);
}
